import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { DEFAULT_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { rem } from 'polished'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import Counter from 'LuxKit/Counter'
import type { CounterChangeEventHandler } from 'LuxKit/Counter'
import BaseButton from 'LuxKit/Button/BaseButton'
import BodyText from 'LuxKit/Typography/BodyText'
import { CaptionMedium, CaptionSmallAlt } from 'LuxKit/Typography/Caption'
import { TYPOGRAPHY_LINE_HEIGHT_CSS_VAR } from 'LuxKit/Typography/Typography'
import AIGlowRing from 'components/Common/AIGlowRing'
import AIGlowHighlightContext from 'contexts/aiGlowHighlightContext'
import {
  getExactHotelNights,
  HOTEL_DEFAULT_CUSTOM_NIGHTS,
  HOTEL_MAX_NIGHTS,
  HOTEL_MEDIUM_STAY_NIGHTS,
  HOTEL_MIN_NIGHTS,
  HOTEL_SHORT_STAY_NIGHTS,
} from 'lib/search/hotelFlexibleDuration'
import AIGlowToggleButton from './AIGlowToggleButton'

const SelectionContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-column-gap: ${rem(12)};
  flex-wrap: wrap;
  gap: ${rem(12)};
  align-items: center;
  justify-content: center;
  width: fit-content;
  margin: auto;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(4, 1fr);
  }

  &[data-hotel-exact='true'] {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;

    > div > [data-option] {
      min-height: ${rem(68)};
    }

    ${mediaQueryUp.desktop} {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }
`

const CustomDurationCell = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(4)};
  min-height: ${rem(68)};
  padding: ${rem(5)} ${rem(8)};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};

  &.is-selected {
    border-color: ${props => props.theme.palette.brand.primary.normal};
    box-shadow: 0 0 0 ${rem(1)} ${props => props.theme.palette.brand.primary.normal};
  }

  &.is-glowing {
    border-color: transparent;
    box-shadow: none;
  }
`

const CustomSelectButton = styled(BaseButton)`
  && {
    min-width: 0;
    min-height: 0;
    padding: 0;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    background: transparent;
  }
`

const CustomCounter = styled(Counter)`
  gap: ${rem(4)};
  border: 0;

  > .variant-text-regular {
    ${CaptionSmallAlt}
    width: ${rem(64)};
    white-space: nowrap;
    line-height: var(${TYPOGRAPHY_LINE_HEIGHT_CSS_VAR});
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

    ${mediaQueryUp.tablet} {
      ${CaptionMedium}
    }
  }

  > button {
    --icon-button-icon-size: ${rem(16)};
    width: ${rem(24)};
    height: ${rem(24)};
    padding: ${rem(3)};
    border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-radius: 50%;
  }
`

interface Props {
  durationOptionsCustom?: Array<DurationOption>
  previouslySelectedDuration?: FLEXIBLE_DURATION_RANGE
  onDurationChange?: (duration: string) => void
  className?: string
  useExactHotelDurations?: boolean
}

export interface DurationOption {
  title: string
  caption: string
  id: FLEXIBLE_DURATION_RANGE
}

function isExactHotelDuration(duration: string): duration is `${number}` {
  return getExactHotelNights(duration) !== undefined
}

export function getDurationOptionsDefault(intl: IntlShape, useExactHotelDurations: boolean = false): Array<DurationOption> {
  return [
    {
      title: intl.formatMessage({
        id: '7EMZX4qO66g-daVbb-Yb',
        defaultMessage: 'Any duration',
        description: 'Duration picker option title for any duration stay',
      }),
      caption: intl.formatMessage({
        id: 'Fn0aJv5lovnCMIOEiNn6',
        defaultMessage: 'I’ll decide later',
        description: 'Duration picker caption for the any duration option',
      }),
      id: DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION,
    },
    {
      title: intl.formatMessage({
        id: 'B1C1izLARRSv6lwQghsh',
        defaultMessage: 'Short stay',
        description: 'Duration picker option title for a short hotel stay or a short stay range in other searches',
      }),
      caption: useExactHotelDurations ? intl.formatMessage({
        id: 'y_jCv934UI5E_O-S52Bv',
        defaultMessage: '2 nights',
        description: 'Exact two-night duration caption for the short stay option in hotel flexible-date search',
      }) : intl.formatMessage({
        id: 'Ac-Ziq7KbhT4cY59TYAl',
        defaultMessage: '1 to 3 nights',
        description: 'Duration picker caption describing the short stay range',
      }),
      id: useExactHotelDurations ? HOTEL_SHORT_STAY_NIGHTS : DEFAULT_FLEXIBLE_DURATION_RANGE.SHORT_STAY,
    },
    {
      title: intl.formatMessage({
        id: '2zztZbNY0wACZaFBt083',
        defaultMessage: 'Medium stay',
        description: 'Duration picker option title for a medium hotel stay or a medium stay range in other searches',
      }),
      caption: useExactHotelDurations ? intl.formatMessage({
        id: 'IP7wWAZUe8OT9-nDqIEL',
        defaultMessage: '5 nights',
        description: 'Exact five-night duration caption for the medium stay option in hotel flexible-date search',
      }) : intl.formatMessage({
        id: 'qtfTfpJ4TBIE6_1-Ac4E',
        defaultMessage: '4 to 7 nights',
        description: 'Duration picker caption describing the medium stay range',
      }),
      id: useExactHotelDurations ? HOTEL_MEDIUM_STAY_NIGHTS : DEFAULT_FLEXIBLE_DURATION_RANGE.MEDIUM_STAY,
    },
    ...(!useExactHotelDurations ? [{
      title: intl.formatMessage({
        id: 'igwdb_-8icUdI6WBaWrt',
        defaultMessage: 'Long stay',
        description: 'Duration picker option title for a long stay (8+ nights)',
      }),
      caption: intl.formatMessage({
        id: 'i0zU8agerMPJnW_VW1Gm',
        defaultMessage: '8+ nights',
        description: 'Duration picker caption describing the long stay range',
      }),
      id: DEFAULT_FLEXIBLE_DURATION_RANGE.LONG_STAY,
    }] : []),
  ]
}

function DurationPicker(props: Props) {
  const intl = useIntl()
  const isAIGlowHighlighted = useContext(AIGlowHighlightContext)
  const { useExactHotelDurations = false, onDurationChange, previouslySelectedDuration } = props
  const defaultDurationOptions = useMemo(() => getDurationOptionsDefault(intl, useExactHotelDurations), [useExactHotelDurations, intl])
  const { durationOptionsCustom = defaultDurationOptions } = props
  const [flexibleNights, setFlexibleNights] =
    useState<FLEXIBLE_DURATION_RANGE>(
      previouslySelectedDuration ?? DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION,
    )
  const [customNights, setCustomNights] = useState(() => {
    const exactNights = getExactHotelNights(previouslySelectedDuration)
    return exactNights && exactNights !== 2 && exactNights !== 5 ? exactNights : HOTEL_DEFAULT_CUSTOM_NIGHTS
  })

  useEffect(() => {
    setFlexibleNights(previouslySelectedDuration ?? DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION)
    const exactNights = getExactHotelNights(previouslySelectedDuration)
    if (exactNights && exactNights !== 2 && exactNights !== 5) setCustomNights(exactNights)
  }, [previouslySelectedDuration])

  const isCustomDurationSelected = useExactHotelDurations && getExactHotelNights(flexibleNights) !== undefined &&
    flexibleNights !== HOTEL_SHORT_STAY_NIGHTS && flexibleNights !== HOTEL_MEDIUM_STAY_NIGHTS
  const isCustomDurationGlowing = isAIGlowHighlighted && isCustomDurationSelected

  const handleCustomDurationSelect = useCallback(() => {
    const duration = customNights.toString()
    if (!isExactHotelDuration(duration)) return
    setFlexibleNights(duration)
    onDurationChange?.(duration)
  }, [customNights, onDurationChange])

  const handleCustomNightChange = useCallback<CounterChangeEventHandler>((nextNights) => {
    const duration = nextNights.toString()
    if (!isExactHotelDuration(duration)) return
    setCustomNights(nextNights)
    setFlexibleNights(duration)
    onDurationChange?.(duration)
  }, [onDurationChange])

  const customDurationLabel = intl.formatMessage({
    id: 'Eg_gGlQgR0EoMVAxkqdF',
    defaultMessage: 'Custom duration',
    description: 'Title of the custom stay-length option in the hotel flexible-date picker',
  })
  const formatCustomNights = useCallback((count: number) => intl.formatMessage({
    id: 'vHXowfVjeAt-zoCLboDP',
    defaultMessage: '{count, plural, one {# night} other {# nights}}',
    description: 'Pluralised night count shown in date picker summary',
  }, { count }), [intl])

  const onDurationClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      const duration = event.currentTarget.dataset.option as FLEXIBLE_DURATION_RANGE
      const newFlexibleDuration = duration === flexibleNights ?
        DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION :
        duration
      setFlexibleNights(newFlexibleDuration)
      onDurationChange?.(newFlexibleDuration)
    },
    [flexibleNights, onDurationChange],
  )

  return (
    <SelectionContainer data-testid="flexibleDurationPicker" data-hotel-exact={useExactHotelDurations} className={props.className}>
      {durationOptionsCustom.map((option) => (
        <AIGlowToggleButton
          key={option.id}
          kind="secondary"
          title={option.title}
          subtitle={option.caption}
          isSelected={option.id === flexibleNights}
          data-testid={`duration-option-${option.id}`}
          data-option={option.id}
          onClick={onDurationClick}
        />
      ))}
      {useExactHotelDurations && <CustomDurationCell className={clsx({ 'is-selected': isCustomDurationSelected, 'is-glowing': isCustomDurationGlowing })} data-testid="duration-option-custom">
        <CustomSelectButton kind="tertiary" size="xsmall" type="button" aria-pressed={isCustomDurationSelected} onClick={handleCustomDurationSelect}>
          <BodyText variant="medium" weight="normal">{customDurationLabel}</BodyText>
        </CustomSelectButton>
        <CustomCounter
          size="small"
          value={customNights}
          min={HOTEL_MIN_NIGHTS}
          max={HOTEL_MAX_NIGHTS}
          onChange={handleCustomNightChange}
          valueFormatter={formatCustomNights}
          data-testid="custom-duration-counter"
        />
        {isCustomDurationGlowing && <AIGlowRing data-testid="ai-glow-toggle-ring" />}
      </CustomDurationCell>}
    </SelectionContainer>
  )
}

export default DurationPicker
